import type { PricePackage } from "./settle";
import type { DeferredCredit, Session, Ticket, TicketKind, TicketOpsStatus } from "./types";

export const SESSION_LABELS: Record<Session, string> = {
  afternoon: "午場",
  evening: "晚場",
};

export const OPS_STATUS_LABELS: Record<Exclude<TicketOpsStatus, "">, string> = {
  onsite_afternoon: "現場午場",
  onsite_evening: "現場晚場",
  onsite_both: "現場通票",
  refund: "退票",
};

export function sessionsFromTicketType(ticketType: string): Session[] {
  if (ticketType.includes("通票")) return ["afternoon", "evening"];
  if (ticketType.includes("晚場")) return ["evening"];
  return ["afternoon"];
}

export function sessionsFromOpsStatus(opsStatus: TicketOpsStatus): Session[] | null {
  if (opsStatus === "onsite_afternoon") return ["afternoon"];
  if (opsStatus === "onsite_evening") return ["evening"];
  if (opsStatus === "onsite_both") return ["afternoon", "evening"];
  if (opsStatus === "refund") return null;
  return null;
}

export function ticketTypeFromOpsStatus(
  opsStatus: TicketOpsStatus,
  originalTicketType: string,
): string {
  if (opsStatus === "onsite_afternoon") return OPS_STATUS_LABELS.onsite_afternoon;
  if (opsStatus === "onsite_evening") return OPS_STATUS_LABELS.onsite_evening;
  if (opsStatus === "onsite_both") return OPS_STATUS_LABELS.onsite_both;
  if (opsStatus === "refund") return `退票（原：${originalTicketType || "預售"}）`;
  return originalTicketType;
}

export function kindFromOpsStatus(opsStatus: TicketOpsStatus, fallback: TicketKind = "presale"): TicketKind {
  if (opsStatus === "refund") return "refund";
  if (opsStatus.startsWith("onsite_")) return "onsite";
  return fallback;
}

/**
 * 現場／公關表：匿名現場票與公關（source=field）。
 * 7/11 延用兌換後的具名票走 source=form，只出現在早鳥／預售。
 * Legacy rows may still be tagged source=field but carry creditSourceId — treat as checkin, not field table.
 */
export function isFieldTableTicket(ticket: Ticket): boolean {
  if (ticket.source !== "field") return false;
  if (ticket.creditSourceId) return false;
  return true;
}

export function remaining(ticket: Ticket, session: Session): number {
  return Math.max(0, ticket.quantity - ticket.checkIns[session].count);
}

export function isComplete(ticket: Ticket): boolean {
  if (ticket.kind === "refund" || ticket.opsStatus === "refund") return true;
  return ticket.sessions.every((session) => remaining(ticket, session) === 0);
}

export function paymentNeedsAttention(ticket: Ticket): boolean {
  if (ticket.kind === "refund" || ticket.opsStatus === "refund") return false;
  if (ticket.kind === "onsite" || ticket.kind === "pr") {
    // Onsite / deferred-credit settle still needs top-up or door refund attention
    if (ticket.settleStatus === "await_topup" || ticket.settleStatus === "await_refund") return true;
    return false;
  }
  if (ticket.opsStatus?.startsWith("onsite_")) return false;
  if (ticket.settleStatus === "await_topup" || ticket.settleStatus === "await_refund") return true;
  if (ticket.paymentMethod.includes("櫃台")) return !ticket.paymentRecorded;
  if (ticket.paymentMethod.includes("匯款")) {
    return !(ticket.paymentRecorded ?? Boolean(ticket.paymentAmount || ticket.paymentProof));
  }
  return false;
}

export function allowsPerformerAssignment(ticket: Ticket): boolean {
  if (ticket.kind === "pr" || ticket.kind === "refund") return false;
  if (ticket.opsStatus === "refund") return false;
  return true;
}

export function allowsCheckIn(ticket: Ticket): boolean {
  return ticket.kind !== "refund" && ticket.opsStatus !== "refund";
}

export function normalizeQuery(value: string): string {
  return value.trim().toLocaleLowerCase("zh-Hant").replace(/[\s\-()]/g, "");
}

export function matchesTicket(ticket: Ticket, query: string): boolean {
  const normalized = normalizeQuery(query);
  if (!normalized) return true;
  return [ticket.id, ticket.name, ticket.phone, ticket.email, ticket.ticketType]
    .map(normalizeQuery)
    .some((value) => value.includes(normalized));
}

/** Unredeemed 7/11 deferred credit; empty query never matches (only show while searching). */
export function matchesDeferredCredit(
  credit: Pick<DeferredCredit, "name" | "phone" | "redeemed">,
  query: string,
): boolean {
  if (credit.redeemed) return false;
  const raw = query.trim();
  if (!raw) return false;
  const digits = raw.replace(/\D/g, "");
  const nameQ = normalizeQuery(raw);
  const phone = String(credit.phone || "").replace(/\D/g, "");
  if (digits) {
    if (digits.length <= 3) {
      if (phone.slice(-3) === digits || phone.endsWith(digits)) return true;
    } else if (phone.includes(digits) || phone.endsWith(digits)) {
      return true;
    }
  }
  if (nameQ && normalizeQuery(credit.name || "").includes(nameQ)) return true;
  return false;
}

export function displayPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return phone || "—";
  if (digits.startsWith("852") && digits.length >= 11) {
    return `+852-${digits.slice(3)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return phone;
}

export function displayTicketName(ticket: Ticket): string {
  if (ticket.name?.trim()) return ticket.name;
  if (ticket.kind === "pr") return "公關票";
  if (ticket.kind === "onsite") return "現場票";
  if (ticket.kind === "refund") return "退票";
  return ticket.id;
}

/** Walk-up 7/11 credit still gets the $50 coupon, even if reconcile left couponEligible false. */
export function couponEligibleOnRedeem(credit: {
  couponEligible?: boolean;
  priorCredit?: number;
  couponIssued?: boolean;
}): boolean {
  return Boolean(credit.couponEligible) || Number(credit.priorCredit) > 0;
}

/** Packages a 7/11 deferred credit can redeem into, given the current check-in session. */
export function deferredRedeemPackages(session: Session): PricePackage[] {
  if (session === "evening") return ["evening"];
  return ["afternoon", "evening", "both"];
}

/** Evening check-in hint: who was named for the same seat at noon (通票). */
export function afternoonNamingHintLabel(afternoonPerformer: string | undefined | null): string {
  const name = (afternoonPerformer || "").trim();
  if (!name || name === "尚未指名") return "午場尚未指名";
  return `午場指名：${name}`;
}
